<script lang="ts" setup>
import PreviewProjectModel from '@/models/PreviewProjectModel'
import { BusEvent, ProjectStatus } from '@/helper/Types'
import { reactive } from 'vue'
import bus from '@/helper/Bus'
import { ModalTypes } from '@/helper/ModalTypes'
import ProjectController from '@/controllers/ProjectController'
import router from '@/helper/router'
import i18n from '@/helper/Translations'
import { formatDate } from '@/helper/filter'
import { calculateDisplayPrice, isDesktop } from '@/helper/tools'
import ActionsMenu from '@/components/projects/ActionsMenu.vue'
import { hj } from '@/helper/analytics'

defineProps<{
  project: PreviewProjectModel
  hidePrices: boolean
}>()

function deleteClicked(projectId: string): void {
  hj('projects_delete')

  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.DELETE,
    confirmEventName: BusEvent.DELETE_CONFIRMED,
    payload: projectId
  })
}

async function openUpdateProductsModal(projectId: string): Promise<void> {
  const updateProducts = await ProjectController.getProjectUpdateProducts(projectId)
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.UPDATE_PROJECT,
    confirmEventName: BusEvent.UPDATE_PROJECT_CONFIRMED,
    payload: reactive({ updateProducts, updateConfirmed: false, projectId })
  })
}

async function editClicked(projectId: string, status: string, e: PointerEvent): Promise<void> {
  if ([ProjectStatus.INCOMPLETE, ProjectStatus.COMPLETE].includes(status as ProjectStatus)) {
    await router.push({
      name: 'project',
      params: { projectId: projectId }
    })
    return
  }
  await openUpdateProductsModal(projectId)
}

async function downloadClicked($toggleButton: HTMLDivElement, projectId: string) {
  hj('projects_download')

  await router.push({
    name: 'DownloadPage',
    state: {
      fromRoute: router.currentRoute.value.path
    },
    params: {
      projectId: projectId,
    }
  })
}

function copyClicked(projectId: string, projectName: string): void {
  hj('projects_copy')

  const copiedName = projectName + ' ' + i18n.global.t('modal.copy_copied_name_suffix').toString()
  bus.$emit(BusEvent.SHOW_MODAL, {
    modalType: ModalTypes.COPY,
    confirmEventName: BusEvent.COPY_CONFIRMED,
    payload: {
      projectId,
      copiedName
    }
  })
}
</script>

<template>
  <div
    :data-id="project.id"
    class="fTable__tr has--td project-item"
    @click="editClicked(project.id, project.status, $event)"
  >
    <div :title="project.name" class="fTable__td column is--name" data-hj-suppress>
      <span>{{ project.name }}</span>
      <div v-if="!isDesktop()" class="mobile-project-meta">
        <div v-if="project.customerName">
          {{ project.customerName }}
        </div>
        <div>{{ formatDate(project.lastModified) }}</div>
      </div>
    </div>
    <div class="fTable__td column is--customer" data-hj-suppress>
      {{ project.customerName }}
    </div>
    <div :title="project.lastModified" class="fTable__td column is--last-edited">
      {{ formatDate(project.lastModified) }}
    </div>
    <div v-if="!hidePrices" class="fTable__td column is--price">
      {{ calculateDisplayPrice(project.totalPrice) }}
    </div>
    <div class="fTable__td column is--status">
      <div
        :class="{
          'is--incomplete': project.status === ProjectStatus.INCOMPLETE,
          'is--complete': project.status === ProjectStatus.COMPLETE,
          'is--update': project.status === ProjectStatus.UPDATE_REQUIRED
        }"
        class="fTable__status small-text"
      >
        {{ $t(`overview.STATUS_${project.status}`) }}
      </div>
    </div>
    <div class="fTable__td column is--actions">
      <ActionsMenu
        :project="project"
        @copy="copyClicked"
        @delete="deleteClicked"
        @download="downloadClicked"
        @edit="editClicked"
      />
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';

.mobile-project-meta {
  @include media('>=tablet') {
    display: none;
  }

  > div {
    font-size: rem-calc($textSmallSize);
    line-height: 1rem;
    color: $textColorLight;
    font-weight: normal;

    &.is--status {
      border: none;
      padding: 0;
      min-width: auto;
      text-align: left;

    }
  }
}

@include media('<tablet') {
  .is--status > div {
    border: none;
    padding: 0;
    min-width: auto;
    text-align: left;
  }

  .fTable .column.is--status {
    padding: .75rem 0 .75rem 1.5rem;
  }

  .is--name {
    min-width: unset !important;
  }
}

</style>
